import Vue from "Vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";

export interface MultiSelectOption {
	value: string;
	label: string;
}

@Component
export default class DropDownSelect extends Vue {
	public listSelected: string[] = [];

	@Prop()
	public listValues!: MultiSelectOption[];

	@Prop({ default: "Filtrer" })
	public placeholder;

	@Prop({ default: undefined })
	public allItemLabel?: string; // Label de l'item "Tous" affiché en tête de liste. Si non défini, l'item n'est pas rendu.

	@Prop({ default: false })
	public allItemChecked!: boolean; // État initial de la sélection : true = tout sélectionné au montage. Ignoré si allItemLabel n'est pas défini.

	public mounted(): void {
		if (this.allItemLabel && this.allItemChecked) {
			this.listSelected = this.listValues.map(v => v.value);
		}
		this.$nextTick(() => {
			this.addTitlesToTags();
		});
	}

	/**
	 * Quand listValues est alimenté après le mount (chargement async),
	 * resynchronise listSelected si allItemChecked est activé.
	 */
	@Watch("listValues")
	public onListValuesChanged(newValues: MultiSelectOption[]): void {
		if (this.allItemLabel && this.allItemChecked && newValues.length > 0 && this.listSelected.length === 0) {
			this.listSelected = newValues.map(v => v.value);
		}
	}

	public updated(): void {
		this.$nextTick(() => {
			this.addTitlesToTags();
		});
	}

	/**
	 * Retourne true si tous les items de la liste sont sélectionnés.
	 * @returns boolean
	 */
	public get isTousChecked(): boolean {
		return this.listValues.length > 0 && this.listSelected.filter((v: string) => v !== "__select_all__").length === this.listValues.length;
	}

	/**
	 * Gère le changement de la liste sélectionnée
	 * @param values - Les valeurs sélectionnées
	 * @returns void
	 */
	public onListSelectedChange(values: string[]): void {
		if (!this.allItemLabel || !values.includes("__select_all__")) {
			return;
		}
		this.listSelected = this.isTousChecked ? [] : this.listValues.map(v => v.value);
	}

	public reset(): void {
		this.listSelected = [];
	}

	/**
	 * Gère le changement d'état d'une option (sélection/désélection)
	 * @param optionValue - La valeur de l'option à ajouter ou retirer
	 * @returns void
	 */
	public handleOptionChange(optionValue: string): (checked: boolean) => void {
		return (checked: boolean) :void => {
			if (checked) {
				this.listSelected = [...this.listSelected, optionValue];
			} else {
				this.listSelected = this.listSelected.filter(v => v !== optionValue);
			}
		};
	}

	/**
	 * Gère l'ouverture/fermeture de la dropdown et émet les changements
	 * @param isVisible - Indique si la dropdown est visible ou non
     * @returns void
	 */
	public handleVisibleChange(isVisible: boolean): void {
		if (!isVisible) {
			this.$emit("dropDownChanged", this.listSelected);
		}
	}

	/**
	 * Gère la suppression d'un tag et émet les changements
	 * @returns void
	 */
	public removeTag(): void {
		this.$emit("dropDownChanged", this.listSelected);
	}

	/**
	 * Ajoute l'attribut title aux tags pour afficher les tooltips au survol
	 * Permet de voir le texte complet des tags tronqués
	 * @returns void
	 */
	private addTitlesToTags(): void {
		const tags = this.$el.querySelectorAll(".el-tag");
		tags.forEach((tag) => {
			if (!tag.getAttribute("title")) {
				tag.setAttribute("title", tag.textContent?.trim() || "");
			}
		});
	}
}



// WEBPACK FOOTER //
// ./src/shared/components/dropDownSelect/dropDownSelect.ts